import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Marquee } from '../components/marquee';
import VideoPlayer from '../components/videoPlayer3';
import { useHideMenu } from '../hooks/useHideMenu';
import { UseSweetAlert } from '../hooks/useSweetAlert';
import { SocketContext } from '../context/SocketContext';
import { getUltimos } from '../helpers/getUltimos';
import { serverApi } from '../api';
import sound from "../assets/new-ticket.mp3";
import { SelectElement } from '../components/selectElement';
import './CallScreen.css';

/**
 * CallScreen Component
 * Carga la misma lógica y sockets que Cola2.js pero con el diseño de pantalla completa
 * inspirado en Video.js (video de fondo a pantalla completa, lista translúcida flotante,
 * anuncio animado del llamado al centro y barra de texto en la parte inferior).
 * Se ejecuta por fuera de la administración ocultando el menú y el navbar.
 */
export const CallScreen = () => {
    // 1. Ocultar Menú y Navbar principal
    useHideMenu(true);

    // 2. Estados de formulario y entidad
    const [formData, setFormData] = useState({
        codigoEntidad: '',
        codigoUbicacion: ''
    });

    const { socket, conectarSocket } = useContext(SocketContext);

    const [entidadStorage, setEntidadStorage] = useState(
        localStorage.getItem('codigoEntidad') || null
    );
    const [ubicaciones, setUbicaciones] = useState([]);
    const [videoList, setVideoList] = useState(
        localStorage.getItem('videos') || ""
    );

    // 3. Estados de tickets, visibilidad y animaciones
    const [tickets, setTickets] = useState([]);
    const [minutosUltimo, setMinutosUltimo] = useState(0);
    const [currentCalledTicket, setCurrentCalledTicket] = useState(null);
    const [llamadoNuevo, setLlamadoNuevo] = useState(false);

    // Estado del ciclo de la lista de turnos (10s visible, 5s oculto)
    const [showTicketsPanel, setShowTicketsPanel] = useState(true);

    // Estado para detectar soporte/bloqueo de sonido por políticas del navegador
    const [showAudioNotice, setShowAudioNotice] = useState(false);

    // Helper para ajustar dinámicamente la fuente si el texto es largo
    const getBadgeFontSize = (text, defaultSize = '1.25rem') => {
        if (!text) return defaultSize;
        const len = text.length;
        if (len > 35) return '0.8rem';
        if (len > 25) return '0.92rem';
        if (len > 18) return '1.05rem';
        return defaultSize;
    };

    // Intento automático y silencioso de desbloquear contexto de audio
    const unlockAudio = useCallback(() => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                const audioCtx = new AudioCtx();
                if (audioCtx.state === 'suspended') {
                    audioCtx.resume().catch(() => {});
                }
            }
            const silentAudio = new Audio(sound);
            silentAudio.volume = 0.01;
            const p = silentAudio.play();
            if (p !== undefined) {
                p.then(() => {
                    silentAudio.pause();
                    setShowAudioNotice(false);
                }).catch(() => {
                    // Si el navegador bloquea la reproducción sin gesto previo
                    setShowAudioNotice(true);
                });
            }
        } catch (err) {
            console.warn("Autoplay audio check warning:", err);
        }
    }, []);

    // Escuchar cualquier interacción o movimiento del usuario para desbloquear automáticamente
    useEffect(() => {
        const handleUserGesture = () => {
            unlockAudio();
            setShowAudioNotice(false);
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };

        window.addEventListener('click', handleUserGesture);
        window.addEventListener('touchstart', handleUserGesture);
        window.addEventListener('keydown', handleUserGesture);
        window.addEventListener('mousemove', handleUserGesture);

        unlockAudio();

        return () => {
            window.removeEventListener('click', handleUserGesture);
            window.removeEventListener('touchstart', handleUserGesture);
            window.removeEventListener('keydown', handleUserGesture);
            window.removeEventListener('mousemove', handleUserGesture);
        };
    }, [unlockAudio]);

    // Reproducción segura de sonido sin romper React si el navegador bloquea
    const reproducirSonido = useCallback(() => {
        try {
            const audio = new Audio(sound);
            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setShowAudioNotice(false);
                    })
                    .catch((err) => {
                        console.warn("Audio play prevenido por autoplay policy:", err);
                        setShowAudioNotice(true);
                    });
            }
        } catch (e) {
            console.warn("Error ejecutando sonido:", e);
            setShowAudioNotice(true);
        }
    }, []);

    // Función nativa para sintetizar voz con manejo de excepciones
    const decirTexto = useCallback((text) => {
        const synth = window.speechSynthesis;
        if (!synth) return;

        const ejecutarVoz = () => {
            try {
                const voices = synth.getVoices();
                const utterThis = new SpeechSynthesisUtterance(text);

                const vocesLatinas = voices.filter(v =>
                    ['es-MX', 'es-CO', 'es-AR', 'es-CL', 'es-PE', 'es-US']
                        .includes(v.lang)
                );

                const vozFemenina = vocesLatinas.find(v =>
                    /paulina|helena|monica|maria|sofia|female/i.test(v.name)
                );

                const vozSeleccionada =
                    vozFemenina ||
                    vocesLatinas[0] ||
                    voices.find(v => v.lang.startsWith('es')) ||
                    voices[0];

                utterThis.voice = vozSeleccionada;
                utterThis.lang = vozSeleccionada?.lang || 'es-CO';
                utterThis.rate = 0.85;
                utterThis.pitch = 1;

                utterThis.onerror = (e) => {
                    console.warn("SpeechSynthesis error event:", e);
                };

                synth.cancel();
                synth.speak(utterThis);
            } catch (err) {
                console.warn("Error en SpeechSynthesis:", err);
            }
        };

        if (synth.getVoices().length > 0) {
            ejecutarVoz();
        } else {
            synth.onvoiceschanged = ejecutarVoz;
        }
    }, []);

    // Temporizador para el ciclo de visibilidad del panel (10 segundos visible / 5 segundos oculto)
    useEffect(() => {
        let timer;
        if (showTicketsPanel) {
            timer = setTimeout(() => {
                setShowTicketsPanel(false);
            }, 10000); // Muestra por 10s
        } else {
            timer = setTimeout(() => {
                setShowTicketsPanel(true);
            }, 5000); // Ocúltalo por 5s
        }
        return () => clearTimeout(timer);
    }, [showTicketsPanel]);

    // Forzar la aparición inmediata del panel cuando llega un llamado
    const forzarMostrarPanel = useCallback(() => {
        setShowTicketsPanel(true);
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        if (e.target.id === 'codigoEntidad') {
            setUbicaciones([]);
            setFormData((prev) => ({
                ...prev,
                codigoEntidad: newValue,
                codigoUbicacion: '',
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [e.target.id]: newValue
            }));
        }
    };

    const fetchUbicaciones = useCallback(async (codigo) => {
        if (!codigo || codigo.trim().length < 3) return;
        try {
            const resp = await serverApi.post(
                `/auth/select-ubicacion-pantallas`,
                { codigoEntidad: codigo.trim() }
            );

            if (!resp.data.ubicaciones || resp.data.ubicaciones.length === 0) return;

            const data = resp.data.ubicaciones.map((item) => ({
                value: item.codigoUbicacion,
                label: item.nombreUbicacion
            }));

            setUbicaciones(data);
        } catch (error) {
            console.error("Error al obtener ubicaciones", error);
        }
    }, []);

    useEffect(() => {
        if (formData.codigoEntidad && formData.codigoEntidad.trim().length >= 3) {
            fetchUbicaciones(formData.codigoEntidad.trim());
        }
    }, [formData.codigoEntidad, fetchUbicaciones]);

    const parseVideos = (videosRaw) => {
        try {
            if (!videosRaw) return "";
            const parsed = typeof videosRaw === "string" ? JSON.parse(videosRaw) : videosRaw;
            return parsed.map(v => v.code).join(',');
        } catch (e) {
            console.error("Error parseando videos:", e);
            return "";
        }
    };

    const ingresar = async () => {
        if (formData.codigoUbicacion === "" || formData.codigoEntidad === "") {
            return UseSweetAlert("Atención!", 'Falta información', 'error');
        }

        const resp = await serverApi.post(
            `/auth/registro-pantallas`,
            formData
        );

        const pantallas = resp.data.pantallas;
        if (!pantallas || pantallas.length === 0) {
            return UseSweetAlert("Atención!", "Revise la información", "error");
        }

        const pantalla = pantallas[0];
        const listaVideos = parseVideos(pantalla.videos);

        localStorage.setItem('codigoEntidad', formData.codigoEntidad);
        localStorage.setItem('codigoUbicacion', formData.codigoUbicacion);
        localStorage.setItem('llamadoVoz', pantalla.llamadoVoz);
        localStorage.setItem('videos', listaVideos);

        conectarSocket({
            query: {
                codigoEntidad: formData.codigoEntidad,
                ubicacion: formData.codigoUbicacion,
            }
        });

        setEntidadStorage(formData.codigoEntidad);
        setVideoList(listaVideos);

        const tks = await getUltimos();
        setTickets(tks ?? []);
    };

    // Cargar lista de videos actualizada
    useEffect(() => {
        const fetchVideos = async () => {
            const codigoEntidad = localStorage.getItem("codigoEntidad");
            const codigoUbicacion = localStorage.getItem("codigoUbicacion");

            if (!entidadStorage) return;

            try {
                const resp = await serverApi.post(
                    `/videos/lista-videos-mostrar`,
                    { codigoEntidad, codigoUbicacion }
                );

                if (resp.data.videos && resp.data.videos.length > 0) {
                    const listaVideos = resp.data.videos.map(v => v.videoCodigo).join(',');
                    localStorage.setItem('videos', listaVideos);
                    setVideoList(listaVideos);
                }
            } catch (error) {
                console.error("Error cargando lista de videos:", error);
            }
        };

        fetchVideos();
    }, [entidadStorage]);

    // Manejo de eventos Sockets
    useEffect(() => {
        if (!socket) return;

        const handleAsignados = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) return;
            const t = ticketsList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setTickets(ticketsList ?? []);
            setCurrentCalledTicket(t);
            setLlamadoNuevo(true);
            forzarMostrarPanel(); // Reaparece el panel si estaba oculto

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            if (t.llamadoVoz === 1) decirTexto(mensaje);
            else reproducirSonido();

            setTimeout(() => {
                setLlamadoNuevo(false);
            }, 9000);
        };

        const handleVolverLlamar = (ticketList) => {
            if (!ticketList || ticketList.length === 0) return;
            const t = ticketList[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setCurrentCalledTicket(t);
            setLlamadoNuevo(true);
            forzarMostrarPanel(); // Reaparece el panel si estaba oculto

            setTickets(prev => {
                const filtered = prev.filter(item => !(item.numeroTicket === t.numeroTicket && item.codigoTipoAtencion === t.codigoTipoAtencion));
                return [t, ...filtered];
            });

            const paciente = t.nombrePaciente?.trim() ||
                `Turno ${t.codigoTipoAtencion} ${t.numeroTicket}`;
            const mensaje = `${paciente}, pase a ${t.moduloClase} ${t.modulo}`;

            if (t.llamadoVoz === 1) decirTexto(mensaje);
            else reproducirSonido();

            setTimeout(() => {
                setLlamadoNuevo(false);
            }, 9000);
        };

        const handleListar = (ticketsList) => {
            if (!ticketsList || ticketsList.length === 0) {
                setTickets([]);
                return;
            }
            const t = ticketsList[0];
            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);

            setTickets(ticketsList ?? []);
        };

        socket.on('ticket-asignado', handleAsignados);
        socket.on('volver-llamar', handleVolverLlamar);
        socket.on('ticket-listado-13', handleListar);

        return () => {
            socket.off('ticket-asignado', handleAsignados);
            socket.off('volver-llamar', handleVolverLlamar);
            socket.off('ticket-listado-13', handleListar);
        };
    }, [socket, decirTexto, reproducirSonido, forzarMostrarPanel]);

    // Carga inicial
    useEffect(() => {
        const load = async () => {
            const tks = await getUltimos();
            if (!tks || tks.length === 0) return;
            const t = tks[0];

            const hoy = new Date();
            const ultimo = new Date(t.llamadoEn);
            const diferencia = (hoy - ultimo) / (1000 * 60);
            setMinutosUltimo(diferencia);
            setTickets(tks ?? []);
        };
        load();
    }, []);

    return (
        <>
            {entidadStorage !== null ? (
                <div className="callscreen-wrapper">
                    {/* Notice flotante discreto si el navegador bloquea audio antes de cualquier movimiento */}
                    {showAudioNotice && (
                        <div
                            className="callscreen-interaction-notice"
                            onClick={() => {
                                unlockAudio();
                                setShowAudioNotice(false);
                            }}
                        >
                            <i className="fa-solid fa-volume-high"></i>
                            <span>Toca en cualquier lugar para habilitar el sonido de los llamados</span>
                        </div>
                    )}

                    {/* 1. Fondo de Video a pantalla completa */}
                    <div className="callscreen-video-bg">
                        <VideoPlayer videos={videoList} />
                    </div>

                    {/* Vignette translúcido */}
                    <div className="callscreen-vignette" />

                    {/* 2. Panel Flotante Transparente / Glassmorphism de la Lista de Turnos */}
                    <div className={`callscreen-tickets-panel ${!showTicketsPanel ? 'callscreen-tickets-panel-hidden' : ''}`}>
                        <div className="callscreen-tickets-header">
                            <div>Turno</div>
                            <div>Módulo</div>
                        </div>

                        <div className="callscreen-tickets-list">
                            {tickets.slice(0, 6).map((ticket, index) => {
                                const textoModulo = `${ticket.moduloClase} ${ticket.modulo}`;
                                const isLatestCall = index === 0 && minutosUltimo < 0.10;
                                const textoTurno = ticket.nombrePaciente && ticket.nombrePaciente.trim() !== ""
                                    ? `${ticket.codigoTipoAtencion}-${ticket.numeroTicket} ${ticket.nombrePaciente}`
                                    : `${ticket.codigoTipoAtencion}-${ticket.numeroTicket} ${ticket.numeroDocumento || ''}`;

                                return (
                                    <div
                                        key={index}
                                        className={`callscreen-ticket-row ${isLatestCall ? 'callscreen-row-active parpadea-futuristic' : ''}`}
                                    >
                                        <div
                                            className="callscreen-badge-turno"
                                            style={{ fontSize: getBadgeFontSize(textoTurno, '1.25rem') }}
                                            title={textoTurno}
                                        >
                                            {textoTurno}
                                        </div>
                                        <div
                                            className="callscreen-badge-modulo"
                                            style={{ fontSize: getBadgeFontSize(textoModulo, '1.15rem') }}
                                            title={textoModulo}
                                        >
                                            {textoModulo}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 3. Popup Futurista del Turno Llamado al Centro */}
                    {llamadoNuevo && currentCalledTicket && (
                        <div className="callscreen-overlay-center">
                            <div className="callscreen-overlay-card parpadea-futuristic">
                                <div className="callscreen-overlay-label">Llamando AHORA</div>
                                <div className="callscreen-overlay-turno">
                                    {currentCalledTicket.codigoTipoAtencion}-{currentCalledTicket.numeroTicket}
                                </div>
                                {currentCalledTicket.nombrePaciente && currentCalledTicket.nombrePaciente.trim() !== "" && (
                                    <div className="callscreen-overlay-paciente">
                                        {currentCalledTicket.nombrePaciente}
                                    </div>
                                )}
                                <div className="callscreen-overlay-modulo-badge">
                                    {currentCalledTicket.moduloClase} {currentCalledTicket.modulo}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 4. Marquee de información en la parte inferior */}
                    <div className="callscreen-marquee-container">
                        <Marquee />
                    </div>
                </div>
            ) : (
                /* Pantalla de login/configuración de la pantalla si no hay entidad configurada */
                <div className="login-container d-flex align-items-center justify-content-center p-3" style={{ minHeight: "100vh", backgroundColor: '#f4f7f6' }}>
                    <div className="col-12 col-md-6 col-lg-4">
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
                            <div className="p-4 text-white text-center" style={{ background: 'linear-gradient(135deg, #0d7367 0%, #0c695e 100%)' }}>
                                <div className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle" style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)' }}>
                                    <i className="fa-solid fa-tv fs-3" style={{ color: '#14b8a6' }}></i>
                                </div>
                                <h4 className="fw-bold m-0" style={{ letterSpacing: '0.5px' }}>Pantalla de Llamado (CallScreen)</h4>
                                <p className="mb-0 opacity-75 small">Configuración de Visualización de Turnos</p>
                            </div>

                            <div className="p-4">
                                <div className="mb-3">
                                    <label htmlFor="codigoEntidad" className="form-label small fw-bold text-secondary">Código de Entidad / Empresa</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="fa-solid fa-building" style={{ color: '#0d7367' }}></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-0 shadow-none"
                                            id="codigoEntidad"
                                            placeholder="Ej: femcc-01"
                                            value={formData.codigoEntidad}
                                            onChange={handleInputChange}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                {ubicaciones.length > 0 && (
                                    <div className='mb-4'>
                                        <label htmlFor="codigoUbicacion" className="form-label small fw-bold text-secondary">Sede / Ubicación</label>
                                        <SelectElement
                                            options={ubicaciones}
                                            id={"codigoUbicacion"}
                                            name={"codigoUbicacion"}
                                            isMulti={false}
                                            closeOnMenuSelect={true}
                                            className={"shadow-sm rounded-3"}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                )}

                                {formData.codigoUbicacion && (
                                    <button
                                        className="btn w-100 py-3 fw-bold text-white shadow-sm rounded-3 transition-all mt-2"
                                        type="button"
                                        onClick={ingresar}
                                        style={{ background: 'linear-gradient(135deg, #0d7367 0%, #0c695e 100%)', border: 'none' }}
                                    >
                                        INGRESAR A LA PANTALLA →
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CallScreen;
